import {
  Button,
  ButtonLink,
  CartItem,
  InsuranceRadioCard,
  PageHeader,
  RadioGroup,
  showToast,
  Typography,
} from '@maia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { getInsurancesOptions } from '../../../../../features/customer/insurances/queryOptions';
import { getCustomerOptions } from '../../../../../features/customer/customer/queryOptions';
import { CustomerInformation } from '../../../../../features/shared/customer/customer-information';
import { useMemo, useState } from 'react';
import { IconAngleRight, IconEnvelope, mapper } from '@maia/icons';
import { useSendSmartLink } from '../../../../../features/claim/claim/send-smartlink.options';
import * as v from 'valibot';
export const Route = createFileRoute(
  '/_authenticated/claims/new/_layout/$customer/',
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { customer: guid } }) => {
    const insurances = await queryClient.ensureQueryData(
      getInsurancesOptions(guid),
    );

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(guid),
    );

    return { insurances, customer };
  },
  validateSearch: v.object({
    insurance: v.optional(v.string()),
  }),
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const { insurance } = Route.useSearch();
  const [chosenInsuranceGuid, setChosenInsuranceGuid] = useState<
    string | undefined
  >(insurance);

  const navigate = Route.useNavigate();

  const { mutateAsync: sendSmartLink, isPending } = useSendSmartLink(
    data.customer.guid,
    () => navigate({ to: '/claims/new/$customer/link-send' }),
    () =>
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet de mail versturen naar de klant. Probeer het later opnieuw',
        config: { position: 'top-right' },
      }),
  );

  const activeInsurance = useMemo(
    () =>
      data.insurances.response.find(({ guid }) => guid === chosenInsuranceGuid),
    [chosenInsuranceGuid, data.insurances.response],
  );

  return (
    <>
      <PageHeader
        className="area-page-header"
        title="Kies verzekering"
        description="Alle actieve verzekeringen"
        backLink={{
          to: '/claims/new',
        }}
      />
      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <RadioGroup
            onChange={setChosenInsuranceGuid}
            value={chosenInsuranceGuid}
          >
            <ul className="flex flex-col gap-y-24">
              {data.insurances.response.map((insurance) => (
                <li key={insurance.guid}>
                  <InsuranceRadioCard
                    value={insurance.guid}
                    name={insurance.title}
                    icon={insurance.icon}
                    reference={insurance.reference}
                    tag={{
                      label: insurance.status.label,
                      variant: insurance.status.variant,
                    }}
                  />
                </li>
              ))}
            </ul>
          </RadioGroup>
        </div>
      </div>

      <div className="flex flex-col area-page-sidebar gap-y-32">
        <Typography
          variant="display6"
          weight="medium"
          className="text-primaryMain"
        >
          Gekozen details
        </Typography>

        {data.customer && <CustomerInformation customer={data.customer} />}

        {activeInsurance && (
          <CartItem
            icon={mapper(activeInsurance.icon)}
            name={activeInsurance.title}
            specs={activeInsurance.specs
              .filter((spec) => !!spec.value)
              .map((spec) => ({
                icon: spec.icon,
                title: spec.label,
                description: spec.value,
              }))}
            billingUnit={data.customer.billingUnit}
          />
        )}

        <div className="flex flex-col mt-auto gap-y-12">
          <Button
            intent="cta"
            size="large"
            iconLeft={IconEnvelope}
            isDisabled={!chosenInsuranceGuid || !data.customer || isPending}
            isLoading={isPending}
            className="w-full justify-center"
            onPress={() => sendSmartLink()}
          >
            Claim link versturen
          </Button>

          <ButtonLink
            to="/claims/new/$customer/$insurance"
            params={{
              customer: data.customer.guid,
              insurance: chosenInsuranceGuid ?? '',
            }}
            intent="primary"
            size="large"
            iconRight={IconAngleRight}
            isDisabled={!chosenInsuranceGuid || !data.customer || isPending}
            className="w-full justify-center"
          >
            Start claim flow
          </ButtonLink>
        </div>
      </div>
    </>
  );
}
